import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { LifeBuoy, ShieldAlert } from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { useToast } from '@/hooks/use-toast';
import { supabase } from '@/integrations/supabase/client';

type SupportSnapshot = {
  suspended_tenants: number;
  open_enquiries: number;
  unread_customer_messages: number;
  recent_actions: Array<{ id: string; organization_name: string | null; action: string; reason: string; created_at: string }>;
};

const PlatformSupport = () => {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const [orgId, setOrgId] = useState('');
  const [action, setAction] = useState('support_note');
  const [reason, setReason] = useState('');

  const { data, isLoading, error } = useQuery({
    queryKey: ['platform-support'],
    queryFn: async () => {
      const { data, error } = await supabase.rpc('get_support_snapshot' as never, { _org_id: null } as never);
      if (error) throw error;
      return data as SupportSnapshot;
    },
  });

  const logMutation = useMutation({
    mutationFn: async () => {
      const { error } = await supabase.rpc('log_support_action' as never, {
        _org_id: orgId || null,
        _action: action,
        _reason: reason,
        _metadata: { source: 'platform_support' },
      } as never);
      if (error) throw error;
    },
    onSuccess: () => {
      setReason('');
      queryClient.invalidateQueries({ queryKey: ['platform-support'] });
      toast({ title: 'Support action logged' });
    },
    onError: () => toast({ title: 'Unable to log support action', description: 'Reason must be at least 8 characters and you must be a platform admin.', variant: 'destructive' }),
  });

  return (
    <div className="space-y-6 p-4 md:p-8">
      <div>
        <h2 className="font-display text-3xl text-foreground">Support tooling</h2>
        <p className="mt-1 text-muted-foreground">Operational snapshot plus footgun-guarded support action logging.</p>
      </div>

      <div className="grid gap-4 md:grid-cols-3">
        <Card><CardContent className="p-4"><p className="text-xs text-muted-foreground">Suspended tenants</p><p className="text-2xl font-semibold">{data?.suspended_tenants ?? 0}</p></CardContent></Card>
        <Card><CardContent className="p-4"><p className="text-xs text-muted-foreground">Open enquiries</p><p className="text-2xl font-semibold">{data?.open_enquiries ?? 0}</p></CardContent></Card>
        <Card><CardContent className="p-4"><p className="text-xs text-muted-foreground">Unread customer messages</p><p className="text-2xl font-semibold">{data?.unread_customer_messages ?? 0}</p></CardContent></Card>
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2"><ShieldAlert className="h-5 w-5 text-primary" /> Footgun-guarded action log</CardTitle>
          <CardDescription>Every sensitive support action must include a reason and is written to an append-only platform log.</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid gap-4 md:grid-cols-2">
            <div className="space-y-2"><Label htmlFor="support-org">Organisation ID (optional)</Label><Input id="support-org" value={orgId} onChange={(event) => setOrgId(event.target.value)} placeholder="uuid" /></div>
            <div className="space-y-2"><Label htmlFor="support-action">Action</Label><Input id="support-action" value={action} onChange={(event) => setAction(event.target.value)} /></div>
          </div>
          <div className="space-y-2"><Label htmlFor="support-reason">Reason</Label><Textarea id="support-reason" value={reason} onChange={(event) => setReason(event.target.value)} placeholder="Required — what are you doing and why?" /></div>
          <Button onClick={() => logMutation.mutate()} disabled={logMutation.isPending || reason.trim().length < 8}>
            <LifeBuoy className="mr-2 h-4 w-4" /> Log support action
          </Button>
        </CardContent>
      </Card>

      <Card>
        <CardHeader><CardTitle>Recent support actions</CardTitle></CardHeader>
        <CardContent className="space-y-3">
          {isLoading ? <p className="text-sm text-muted-foreground">Loading support snapshot...</p> : null}
          {error ? <p className="text-sm text-destructive">Unable to load support tooling.</p> : null}
          {(data?.recent_actions || []).length === 0 ? <p className="text-sm text-muted-foreground">No support actions logged yet.</p> : null}
          {(data?.recent_actions || []).map((entry) => (
            <div key={entry.id} className="rounded-md border p-3">
              <div className="flex flex-wrap items-center gap-2"><Badge variant="outline">{entry.action}</Badge><span className="text-sm font-medium">{entry.organization_name || 'Platform-wide'}</span></div>
              <p className="mt-2 text-sm text-muted-foreground">{entry.reason}</p>
              <p className="mt-1 text-xs text-muted-foreground">{new Date(entry.created_at).toLocaleString()}</p>
            </div>
          ))}
        </CardContent>
      </Card>
    </div>
  );
};

export default PlatformSupport;
